/**
 * amobler-gallery.js
 *
 * Listens for `variant:change:{sectionId}:{productId}` custom events
 * dispatched by `<block-variant-picker>` (see assets/block.product-variant-picker.js
 * lines 35, 46), and rebuilds the Amøbler gallery's Flickity slides from
 * a hidden <template> pool using the embedded variant→media-id JSON map.
 *
 * This skeleton only logs on event receipt. Task 9 implements the actual
 * Flickity swap. Idempotent init pattern matches assets/amobler-variant-row.js.
 */
(function () {
  if (window.__amoblerGalleryInit) return;
  window.__amoblerGalleryInit = true;

  const GALLERY_ATTR = 'data-amobler-gallery';
  const MAP_ATTR = 'data-amobler-variant-media-map';
  const POOL_ATTR = 'data-amobler-gallery-pool';
  const POOL_ITEM_ATTR = 'data-amobler-pool-media-id';
  const THUMB_POOL_ATTR = 'data-amobler-gallery-thumb-pool';
  const THUMB_ITEM_ATTR = 'data-amobler-thumb-id';

  function getPool(sectionId) {
    const tmpl = document.querySelector(
      `template[${POOL_ATTR}="${CSS.escape(sectionId)}"]`
    );
    return tmpl ? tmpl.content : null;
  }

  function clonePoolItem(pool, mediaId) {
    if (!pool) return null;
    const src = pool.querySelector(
      `[${POOL_ITEM_ATTR}="${CSS.escape(mediaId)}"]`
    );
    if (!src) return null;
    return src.firstElementChild ? src.firstElementChild.cloneNode(true) : src.cloneNode(true);
  }

  function getThumbPool(sectionId) {
    const tmpl = document.querySelector(
      `template[${THUMB_POOL_ATTR}="${CSS.escape(sectionId)}"]`
    );
    return tmpl ? tmpl.content : null;
  }

  function cloneThumb(thumbPool, mediaId) {
    if (!thumbPool) return null;
    const src = thumbPool.querySelector(
      `[${THUMB_ITEM_ATTR}="${CSS.escape(mediaId)}"]`
    );
    return src ? src.cloneNode(true) : null;
  }

  function findFlickity(gallery) {
    const slideshow = gallery.querySelector('[data-product-photos]');
    if (!slideshow || typeof window.Flickity !== 'function') return null;
    return window.Flickity.data(slideshow);
  }

  function readMap(sectionId) {
    const node = document.querySelector(
      `script[${MAP_ATTR}="${CSS.escape(sectionId)}"]`
    );
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.warn('[amobler-gallery] invalid map JSON', err);
      return null;
    }
  }

  function findGallery(sectionId, productId) {
    return document.querySelector(
      `[${GALLERY_ATTR}="${CSS.escape(sectionId + '|' + productId)}"]`
    );
  }

  function abortStockGalleryListeners(gallery) {
    // Stock's product.gallery.js registers updateVariantImage and updateImageSet
    // as listeners on `variant:change:{section}:{product}` with
    // { signal: this.abortController.signal } (product.gallery.js:91-103).
    // Aborting that signal removes them — no prototype mutation needed.
    if (gallery.__amoblerAborted) return;
    gallery.__amoblerAborted = true;
    if (gallery.abortController && typeof gallery.abortController.abort === 'function') {
      gallery.abortController.abort();
    }
  }

  function patchSlideshowSafety(gallery) {
    // Two Slideshow callbacks need defensive wrapping during our swap; both
    // fire synchronously from inside flickity.remove/append, so if either
    // throws unguarded the carousel swap aborts mid-way.
    //
    // 1) args.callbacks.onChange (ProductImages.onSlideChange) crashes with
    //    'prevSlide.setAttribute' when our flickity.remove(allCells) clears
    //    every slide from DOM — its prevSlide lookup queries
    //    .product-main-slide[data-index="currentSlideIndex"] which finds
    //    nothing once the slider is empty. Always fires on every swap;
    //    structural slide shape doesn't change this (the prev slide simply
    //    isn't in the DOM anymore).
    // 2) childNavGoTo crashes with 'el.classList' when slideChange fires for
    //    an index out of bounds for the current childNavLinks — happens when
    //    Flickity selects a high index during append before we've rebuilt
    //    the thumb strip. Re-init at swap-end fixes the active highlight.
    if (gallery.__amoblerSlideshowPatched) return;
    gallery.__amoblerSlideshowPatched = true;
    const sw = gallery.flickity;
    if (!sw) return;
    if (sw.args?.callbacks?.onChange) {
      const origOnChange = sw.args.callbacks.onChange;
      sw.args.callbacks.onChange = function (index) {
        try {
          origOnChange(index);
        } catch (err) {
          // Swallow null-deref from prevSlide lookup after our remove.
        }
      };
    }
    if (typeof sw.childNavGoTo === 'function') {
      const origChildNavGoTo = sw.childNavGoTo.bind(sw);
      sw.childNavGoTo = function (index) {
        try {
          origChildNavGoTo(index);
        } catch (err) {
          // Swallow stale-index access — re-init at swap-end fixes highlighting.
        }
      };
    }
  }

  function handleVariantChange(eventName, variantId) {
    if (variantId == null || variantId === '') return;
    const match = eventName.match(/^variant:change:([^:]+):([^:]+)$/);
    if (!match) return;
    const [, sectionId, productId] = match;
    const gallery = findGallery(sectionId, productId);
    if (!gallery) return;
    const map = readMap(sectionId);
    if (!map) {
      console.warn('[amobler-gallery] no map for section', sectionId);
      return;
    }
    const ids = map[String(variantId)];
    if (!ids) {
      console.warn('[amobler-gallery] no map entry for variant', variantId);
      return;
    }
    const pool = getPool(sectionId);
    if (!pool) {
      console.warn('[amobler-gallery] no pool for section', sectionId);
      return;
    }

    const flickity = findFlickity(gallery);
    const slideshow = gallery.querySelector('[data-product-photos]');
    if (!slideshow) return;

    // Build new slides from the pool. Each pool item was rendered with
    // loopIndex0=0, so all clones come with data-index="0"; rewrite each
    // clone's data-index to its position so stock's thumb-click → goToSlide
    // lookup works after our swap.
    const newSlides = [];
    for (let i = 0; i < ids.length; i += 1) {
      const node = clonePoolItem(pool, ids[i]);
      if (node) {
        if (node.setAttribute) node.setAttribute('data-index', String(i));
        newSlides.push(node);
      }
    }

    if (flickity) {
      // Flickity uses a viewport pattern — actual cells live inside
      // .flickity-viewport > .flickity-slider, NOT as direct children of
      // [data-product-photos]. Use flickity.cells, the canonical list, to
      // get the cell elements for removal.
      const cellElements = (flickity.cells || []).map((c) => c.element);
      flickity.remove(cellElements);
      flickity.append(newSlides);
      flickity.select(0, true, true);
    } else {
      // First-click race: Flickity may not have attached yet. Try once
      // on the next animation frame, then fall back to raw DOM swap.
      requestAnimationFrame(() => {
        const f2 = findFlickity(gallery);
        if (f2) {
          const cellElements2 = (f2.cells || []).map((c) => c.element);
          f2.remove(cellElements2);
          f2.append(newSlides);
          f2.select(0, true, true);
        } else {
          console.warn('[amobler-gallery] Flickity not available, doing raw DOM swap');
          slideshow.innerHTML = '';
          newSlides.forEach((n) => slideshow.appendChild(n));
        }
      });
    }

    // Sync thumbnail strip.
    const thumbScroller = gallery.querySelector('.product__thumbs--scroller');
    if (thumbScroller) {
      const thumbPool = getThumbPool(sectionId);
      const newThumbs = [];
      for (let i = 0; i < ids.length; i += 1) {
        const node = cloneThumb(thumbPool, ids[i]);
        if (node) {
          node.setAttribute('data-index', String(i));
          // Also update the inner <a>'s data-index, since Expanse's thumb click
          // handler reads that attribute.
          const innerA = node.querySelector('a[data-product-thumb]');
          if (innerA) innerA.setAttribute('data-index', String(i));
          newThumbs.push(node);
        }
      }
      const thumbFlickity =
        typeof window.Flickity === 'function'
          ? window.Flickity.data(thumbScroller)
          : null;
      if (thumbFlickity) {
        // Use flickity.cells (NOT thumbScroller.children) — same viewport-
        // pattern lesson from Task 9. children gives you the wrapper, not cells.
        const cellElements = (thumbFlickity.cells || []).map((c) => c.element);
        thumbFlickity.remove(cellElements);
        thumbFlickity.append(newThumbs);
        thumbFlickity.select(0, true, true);
      } else {
        // No Flickity on the scroller — direct DOM replace.
        thumbScroller.innerHTML = '';
        newThumbs.forEach((n) => thumbScroller.appendChild(n));
      }
    }

    // If a zoom modal is open, close it — its image is stale after swap.
    const zoomModal = document.querySelector('product-zoom[open], dialog.product-zoom[open]');
    if (zoomModal) {
      if (typeof zoomModal.close === 'function') {
        zoomModal.close();
      } else {
        zoomModal.removeAttribute('open');
      }
    }

    // Rebind stock listeners that were attached per-element. Cloned nodes
    // have no JS listeners (cloneNode skips them), so thumb clicks and the
    // zoom-on-image click stop working after the first swap unless we
    // re-attach them to the new DOM.
    //
    // 1) Slideshow childNav: re-query childNavLinks from the now-updated
    //    thumb scroller, then call initChildNav to attach per-link click
    //    handlers (preventDefault + goToSlide on the main slideshow).
    const sw = gallery.flickity;
    if (sw && typeof sw.initChildNav === 'function' && sw.args?.childNav) {
      sw.childNavEls = sw.args.childNav.querySelectorAll('.product__thumb-item:not(.hide)');
      sw.childNavLinks = sw.args.childNav.querySelectorAll('.product__thumb-item:not(.hide) a');
      if (sw.childNavLinks.length) {
        sw.args.initialIndex = 0; // reset so initChildNav doesn't index out of bounds
        try {
          sw.initChildNav();
        } catch (err) {
          console.warn('[amobler-gallery] initChildNav failed', err);
        }
      }
    }
    // 2) Photoswipe: re-attach click listeners to the new .js-photoswipe__zoom
    //    triggers inside the new slides.
    if (gallery.photoswipe && typeof gallery.photoswipe.init === 'function') {
      try {
        gallery.photoswipe.init();
      } catch (err) {
        console.warn('[amobler-gallery] photoswipe re-init failed', err);
      }
    }

    // Dispatch a public hook event so future consumers can react.
    gallery.dispatchEvent(new CustomEvent('amobler:gallery:swapped', {
      detail: { variantId, sectionId, productId, mediaIds: ids },
      bubbles: true,
    }));
  }

  function registerListeners(gallery) {
    const tuple = gallery.getAttribute(GALLERY_ATTR);
    if (!tuple) return;
    // Abort stock gallery listeners and patch the slideshow eagerly, before
    // any variant:change fires. Stock's listener registers at <product-images>
    // connectedCallback time (synchronously during HTML parse), so it fires
    // BEFORE our handler if we wait for the first variant:change to suppress.
    abortStockGalleryListeners(gallery);
    patchSlideshowSafety(gallery);
    const [sectionId, productId] = tuple.split('|');
    const eventName = `variant:change:${sectionId}:${productId}`;
    const key = `__amoblerGalleryBound_${sectionId}_${productId}`;
    if (window[key]) return;
    window[key] = true;
    document.addEventListener(eventName, (e) => {
      handleVariantChange(e.type, e.detail?.variant?.id);
    });
  }

  function initAll() {
    document
      .querySelectorAll(`[${GALLERY_ATTR}]`)
      .forEach((gallery) => registerListeners(gallery));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections; re-bind when shopify:section:load fires.
  document.addEventListener('shopify:section:load', initAll);
})();
